import React, { useState, useEffect, useCallback } from 'react';
import { stripQuillColors } from '../../lib/utils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  ThumbsUp,
  Edit,
  Trash2,
  AlertTriangle,
  User,
  Clock,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/Skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { ForumPostForm } from './components/ForumPostForm';
import { ForumComentarioItem } from './components/ForumComentarioItem';
import {
  ForumPost,
  ForumComentario,
  ForumCategoriaLabels,
  ForumCategoriaColors,
  UpdateForumPostData,
} from '../../types/forum';
import {
  getForumPost,
  getForumComentarios,
  updateForumPost,
  moderateForumPost,
  deleteForumPost,
  createForumComentario,
  replyForumComentario,
  updateForumComentario,
  deleteForumComentario,
  moderateForumComentario,
  togglePostLike,
  toggleComentarioLike,
} from '../../services/forumService';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';

export function ForumPostView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { militar } = useAuth();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  const [post, setPost] = useState<ForumPost | null>(null);
  const [comentarios, setComentarios] = useState<ForumComentario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiking, setIsLiking] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  const canModerate = hasPermission('forum.moderar');
  const canDeleteAny = hasPermission('forum.excluir_qualquer');
  const canDeleteOwn = hasPermission('forum.excluir_proprio');
  const isOwner = militar?.id === post?.militar_id;
  const canEdit = isOwner && post?.ativo;
  const canDelete = canDeleteAny || (canDeleteOwn && isOwner);

  const loadPost = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [postData, comentariosData] = await Promise.all([
        getForumPost(id),
        getForumComentarios(id),
      ]);
      setPost(postData);
      setComentarios(comentariosData);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao carregar post',
        variant: 'destructive',
      });
      navigate('/forum');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const handleLikePost = async () => {
    if (!post) return;
    setIsLiking(true);
    try {
      const result = await togglePostLike(post.id);
      setPost((prev) =>
        prev ? { ...prev, user_liked: result.liked, likes_count: result.likes_count } : prev
      );
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao curtir post',
        variant: 'destructive',
      });
    } finally {
      setIsLiking(false);
    }
  };

  const handleUpdatePost = async (data: UpdateForumPostData) => {
    if (!post) return;
    setIsSubmitting(true);
    try {
      const updated = await updateForumPost(post.id, data);
      setPost(updated);
      setShowEditDialog(false);
      toast({
        title: 'Sucesso',
        description: 'Post atualizado com sucesso!',
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao atualizar post',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeratePost = async () => {
    if (!post) return;
    setIsSubmitting(true);
    try {
      const updated = await moderateForumPost(post.id, !post.ativo);
      setPost(updated);
      toast({
        title: 'Sucesso',
        description: `Post ${updated.ativo ? 'ativado' : 'inativado'} com sucesso!`,
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao moderar post',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!post) return;
    setIsSubmitting(true);
    try {
      await deleteForumPost(post.id);
      toast({
        title: 'Sucesso',
        description: 'Post excluído com sucesso!',
      });
      navigate('/forum');
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao excluir post',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleAddComment = async () => {
    if (!post || !newComment.trim()) return;
    setIsAddingComment(true);
    try {
      await createForumComentario(post.id, { conteudo: newComment });
      setNewComment('');
      await loadPost();
      toast({
        title: 'Sucesso',
        description: 'Comentário adicionado com sucesso!',
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao adicionar comentário',
        variant: 'destructive',
      });
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleReplyComment = async (comentarioId: string, conteudo: string) => {
    await replyForumComentario(comentarioId, { conteudo });
    await loadPost();
    toast({
      title: 'Sucesso',
      description: 'Resposta adicionada com sucesso!',
    });
  };

  const handleEditComment = async (comentarioId: string, conteudo: string) => {
    await updateForumComentario(comentarioId, conteudo);
    await loadPost();
    toast({
      title: 'Sucesso',
      description: 'Comentário atualizado com sucesso!',
    });
  };

  const handleDeleteComment = async (comentarioId: string) => {
    await deleteForumComentario(comentarioId);
    await loadPost();
    toast({
      title: 'Sucesso',
      description: 'Comentário excluído com sucesso!',
    });
  };

  const handleModerateComment = async (comentarioId: string, ativo: boolean) => {
    await moderateForumComentario(comentarioId, ativo);
    await loadPost();
    toast({
      title: 'Sucesso',
      description: `Comentário ${ativo ? 'ativado' : 'inativado'} com sucesso!`,
    });
  };

  const handleLikeComment = async (comentarioId: string) => {
    try {
      const result = await toggleComentarioLike(comentarioId);

      const updateComentarios = (prevComentarios: ForumComentario[]): ForumComentario[] =>
        prevComentarios.map((c) => {
          if (c.id === comentarioId) {
            return { ...c, user_liked: result.liked, likes_count: result.likes_count };
          }
          if (c.respostas) {
            return { ...c, respostas: updateComentarios(c.respostas) };
          }
          return c;
        });

      setComentarios(updateComentarios);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao curtir comentário',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-32 mb-6" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-8 w-3/4 mt-2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full mt-2" />
            <Skeleton className="h-4 w-2/3 mt-2" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Post não encontrado</p>
            <Button className="mt-4" onClick={() => navigate('/forum')}>
              Voltar ao Fórum
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const timeAgo = formatDistanceToNow(new Date(post.criado_em), {
    addSuffix: true,
    locale: ptBR,
  });

  // Filtrar apenas comentários de nível superior (sem comentario_pai_id)
  const topLevelComentarios = comentarios.filter((c) => !c.comentario_pai_id);

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl">
      <Button variant="ghost" className="mb-4" onClick={() => navigate('/forum')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar ao Fórum
      </Button>

      <Card className={cn(!post.ativo && "opacity-60")}>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={cn(ForumCategoriaColors[post.categoria], "text-white")}>
                  {ForumCategoriaLabels[post.categoria]}
                </Badge>
                {!post.ativo && <Badge variant="destructive">Inativo</Badge>}
              </div>
              <h1 className="text-2xl font-bold">{post.titulo}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  <span>{post.militar?.patente?.patente} {post.militar?.nick || 'Anônimo'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{timeAgo}</span>
                </div>
              </div>
            </div>

            {(canEdit || canModerate || canDelete) && (
              <div className="flex gap-1">
                {canEdit && (
                  <Button variant="ghost" size="sm" onClick={() => setShowEditDialog(true)}>
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                )}
                {canModerate && (
                  <Button variant="ghost" size="sm" onClick={handleModeratePost} disabled={isSubmitting}>
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    {post.ativo ? 'Inativar' : 'Ativar'}
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Excluir
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          <div
            className="prose prose-sm dark:prose-invert max-w-none forum-content"
            dangerouslySetInnerHTML={{ __html: stripQuillColors(post.conteudo) }}
          />
        </CardContent>

        <CardFooter className="border-t pt-4">
          <Button
            variant="ghost"
            size="sm"
            className={cn(post.user_liked && "text-primary")}
            onClick={handleLikePost}
            disabled={isLiking}
          >
            <ThumbsUp className={cn("h-4 w-4 mr-2", post.user_liked && "fill-current")} />
            {post.likes_count || post.likes?.length || 0} curtidas
          </Button>
        </CardFooter>
      </Card>

      {/* Seção de Comentários */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <MessageSquare className="h-5 w-5" />
          Comentários ({topLevelComentarios.length})
        </h2>

        {/* Formulário para novo comentário */}
        {post.ativo && hasPermission('forum.comentar') && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Escreva um comentário..."
                rows={3}
              />
              <div className="flex justify-end mt-3">
                <Button
                  onClick={handleAddComment}
                  disabled={isAddingComment || !newComment.trim()}
                >
                  {isAddingComment ? 'Enviando...' : 'Comentar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de comentários */}
        {topLevelComentarios.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">
                Nenhum comentário ainda. Seja o primeiro a comentar!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {topLevelComentarios.map((comentario) => (
              <Card key={comentario.id}>
                <CardContent className="pt-6">
                  <ForumComentarioItem
                    comentario={comentario}
                    onLike={handleLikeComment}
                    onReply={handleReplyComment}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                    onModerate={handleModerateComment}
                    canModerate={canModerate}
                    canDelete={canDeleteAny || (canDeleteOwn && comentario.militar_id === militar?.id)}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog de edição */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Post</DialogTitle>
          </DialogHeader>
          <ForumPostForm
            initialData={post}
            onSubmit={handleUpdatePost}
            onCancel={() => setShowEditDialog(false)}
            isLoading={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir post?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Esta ação não pode ser desfeita. O post e todos os seus comentários serão
            permanentemente excluídos.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeletePost} disabled={isSubmitting}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
